import { useEffect, useState, useCallback } from "react";
import { Routes, Route, useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, FileText, FileSpreadsheet, FileType2, Compass, BookOpen, Lock, Download } from "lucide-react";
import { api, apiError, p2FileUrl } from "../lib/api";

const CLASS_STYLE = {
  "PRIVATE WORKING DOCUMENT": "border-ink/40 text-ink",
  "TEAM-FACING": "border-accent/60 text-accent",
  "PRINCIPAL-FACING": "border-[#6b7d6b] text-[#4d5d4d]",
  "MIXED": "border-ink/30 text-ink-soft",
};

function ClassificationNotice({ r }) {
  const label = r.classification === "MIXED"
    ? `Mixed classification — by ${r.mixed_by}`
    : r.classification;
  return (
    <div className={`inline-flex items-center gap-2 border px-3 py-1 text-[10px] uppercase tracking-[0.18em] ${CLASS_STYLE[r.classification] || "border-line text-ink-soft"}`}>
      <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-current" />
      {label}
      {r.classification === "MIXED" && <span className="normal-case tracking-normal text-[11px] text-ink-soft">· copy the approved section before sharing</span>}
    </div>
  );
}

const FormatIcon = ({ f, ...p }) => f === "XLSX" ? <FileSpreadsheet {...p} /> : f === "DOCX" ? <FileType2 {...p} /> : <FileText {...p} />;
const formatLabel = (f) => f === "XLSX" ? "Editable spreadsheet" : f === "DOCX" ? "Editable Word document" : "PDF — displayed & downloadable";

async function openP2(slug, download) {
  try {
    const url = await p2FileUrl(slug, download);
    window.open(url, "_blank", "noopener");
  } catch (e) { toast.error(apiError(e)); }
}

// Approved teaching, rendered responsively (verbatim; source HTML never exposed).
function Lesson({ lesson }) {
  if (!lesson) return null;
  return (
    <div data-testid="module-lesson" className="mt-10">
      <p className="text-[11px] uppercase tracking-[0.2em] text-ink-soft flex items-center gap-2 mb-4"><BookOpen size={14} className="text-accent" /> The Lesson</p>
      {lesson.purpose && (
        <div className="border-l-2 border-accent pl-6 mb-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Purpose</p>
          <p className="mt-2 text-lg font-light text-ink leading-relaxed max-w-2xl">{lesson.purpose}</p>
        </div>
      )}
      {lesson.outcomes?.length > 0 && (
        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-3">In this module</p>
          <ul className="space-y-2 max-w-2xl">
            {lesson.outcomes.map((o, i) => (
              <li key={i} className="flex items-start gap-3 text-ink font-light"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-accent shrink-0" /> <span dangerouslySetInnerHTML={{ __html: o }} /></li>
            ))}
          </ul>
        </div>
      )}
      <div className="lesson-prose max-w-2xl" dangerouslySetInnerHTML={{ __html: lesson.lesson_html }} />
    </div>
  );
}

function ResourcePanel({ r }) {
  const isCompanion = r.role === "companion";
  return (
    <div data-testid={`resource-${r.slug}`} className={`border ${isCompanion ? "border-line ml-0 lg:ml-8 bg-surface/60" : "border-ink/15 bg-surface"} p-6`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4">
          <FormatIcon f={r.format} size={22} className="mt-1 text-accent shrink-0" />
          <div>
            {isCompanion && <p className="text-[10px] uppercase tracking-[0.2em] text-accent mb-1">{r.companion_label || "Editable companion"}</p>}
            {r.repeated && <p className="text-[10px] uppercase tracking-[0.2em] text-ink-soft mb-1">{r.continue_label || "Continue working in this resource"}</p>}
            <h4 className="font-serif text-xl text-ink leading-snug">{r.title}</h4>
            <p className="mt-1 text-xs uppercase tracking-[0.16em] text-ink-soft">{formatLabel(r.format)}</p>
            <p className="mt-3 text-sm font-light text-ink-soft leading-relaxed max-w-xl">{r.description}</p>
            {r.section_note && <p className="mt-2 text-xs italic text-ink-soft">Use the sections noted: {r.section_note}.</p>}
            {r.mixed_note && <p className="mt-2 text-xs italic text-ink-soft">{r.mixed_note}</p>}
            {r.tab_note && (
              <div className="mt-3 border border-line bg-bg/60 px-4 py-3" data-testid={`tab-guidance-${r.slug}`}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-accent">Which tab to open</p>
                <p className="mt-1 text-sm font-light text-ink leading-relaxed">{r.tab_note}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <ClassificationNotice r={r} />
        <div className="ml-auto flex items-center gap-3">
          {!r.ready ? (
            <span className="inline-flex items-center gap-2 border border-line px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-ink-soft cursor-default select-none">
              <Lock size={11} /> Not available
            </span>
          ) : r.format === "PDF" && r.display ? (
            <>
              <button data-testid={`view-${r.slug}`} onClick={() => openP2(r.slug, false)}
                className="inline-flex items-center gap-2 border border-ink px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] text-ink hover:bg-ink hover:text-bg transition-colors">
                <BookOpen size={12} /> Open reading view
              </button>
              <button data-testid={`download-${r.slug}`} onClick={() => openP2(r.slug, true)}
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ink-soft hover:text-ink transition-colors">
                <Download size={12} /> Download PDF
              </button>
            </>
          ) : (
            <button data-testid={`download-${r.slug}`} onClick={() => openP2(r.slug, true)}
              className="inline-flex items-center gap-2 bg-ink text-bg px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors">
              <Download size={12} /> Download {r.format === "XLSX" ? "spreadsheet" : "Word document"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ProgressLine({ done, total }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">
        <span>{done} of {total} modules complete</span><span>{pct}%</span>
      </div>
      <div className="h-px w-full bg-line"><div className="h-px bg-accent transition-all" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

// ---------- Dashboard ----------
function Dashboard({ course, chosen, completed, onChoosePath }) {
  const nav = useNavigate();
  const pA = course.paths.find((p) => p.key === "A");
  const pB = course.paths.find((p) => p.key === "B");
  const doneA = (completed.A || []).length, doneB = (completed.B || []).length;
  const chosenPathObj = course.paths.find((p) => p.key === chosen);
  const nextModule = chosenPathObj?.modules.find((m) => !(completed[chosen] || []).includes(m.id));

  const PathCard = ({ p, done }) => (
    <div data-testid={`path-card-${p.key}`} className="border border-line p-8 lg:p-10 flex flex-col bg-surface/50 hover:border-ink/40 transition-colors">
      <p className="text-[11px] uppercase tracking-ultra text-accent">Path {p.key}</p>
      <h3 className="mt-3 font-serif font-light text-3xl lg:text-4xl text-ink tracking-tight">{p.title}</h3>
      <p className="mt-4 text-base font-light text-ink-soft leading-relaxed flex-1">{p.summary}</p>
      <div className="mt-6"><ProgressLine done={done} total={p.module_count} /></div>
      <button data-testid={`begin-path-${p.key}`} onClick={() => onChoosePath(p.key, true)}
        className="group mt-7 inline-flex items-center gap-3 bg-ink text-bg px-7 py-3.5 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors self-start">
        {done > 0 ? "Continue" : p.cta} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
      </button>
    </div>
  );

  return (
    <div data-testid="p2-dashboard">
      <p className="text-[11px] uppercase tracking-ultra text-accent">Il Salotto Digitale · Course</p>
      <h1 className="mt-4 font-serif font-light text-4xl lg:text-6xl text-ink tracking-tight leading-[1.02]">Welcome to Becoming an Estate Manager™</h1>
      <p className="mt-6 max-w-2xl text-lg font-light text-ink-soft leading-relaxed">
        This course supports two different professional situations. Begin with the path that reflects where you are now.
      </p>

      {chosen && nextModule && (
        <div className="mt-10 border border-ink/15 bg-surface p-6 flex items-center justify-between gap-6 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">Continue where you left off</p>
            <p className="mt-1 font-serif text-xl text-ink">Path {chosen} · {nextModule.label} — {nextModule.title}</p>
          </div>
          <button data-testid="continue-btn" onClick={() => nav(`/becoming/${chosen}/${nextModule.id}`)}
            className="group inline-flex items-center gap-3 border border-ink px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bg transition-colors">
            Resume <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      )}

      <div className="mt-12 grid lg:grid-cols-2 gap-6">
        <PathCard p={pA} done={doneA} />
        <PathCard p={pB} done={doneB} />
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3">
        <span className="text-base font-light text-ink-soft">Not sure which path to choose?</span>
        <Link data-testid="open-navigator" to="/becoming/navigator" className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink link-underline">
          <Compass size={15} className="text-accent" /> Open the Course Navigator
        </Link>
      </div>
    </div>
  );
}

// ---------- Path view (module list + capstone + completion banner) ----------
function PathView({ course, completed }) {
  const { pathKey } = useParams();
  const nav = useNavigate();
  const p = course.paths.find((x) => x.key === pathKey);
  if (!p) return <p className="text-ink-soft">Path not found.</p>;
  const done = completed[pathKey] || [];
  const modulesDone = p.modules.filter((m) => done.includes(m.id)).length;
  const allModulesDone = modulesDone === p.modules.length;
  const capstoneDone = done.includes("capstone");

  return (
    <div data-testid={`path-view-${p.key}`}>
      <button onClick={() => nav("/becoming")} className="text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline flex items-center gap-1 mb-8"><ArrowLeft size={13} /> Dashboard</button>
      <p className="text-[11px] uppercase tracking-ultra text-accent">Path {p.key}</p>
      <h1 className="mt-3 font-serif font-light text-4xl lg:text-5xl text-ink tracking-tight">{p.title}</h1>
      <p className="mt-5 max-w-2xl text-base font-light text-ink-soft leading-relaxed">{p.summary}</p>
      <div className="mt-8 max-w-md"><ProgressLine done={modulesDone} total={p.modules.length} /></div>

      {allModulesDone && (
        <div data-testid="path-completion" className="mt-10 border border-accent/40 bg-accent/5 p-8">
          <h2 className="font-serif font-light text-2xl text-ink">{p.completion.heading}</h2>
          <p className="mt-4 text-sm font-light text-ink-soft">You have developed:</p>
          <ul className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-1.5 max-w-2xl">
            {p.completion.confirms.map((c) => (
              <li key={c} className="flex items-start gap-2 text-ink font-light text-sm"><Check size={14} className="mt-1 text-accent shrink-0" /> {c}</li>
            ))}
          </ul>
          {p.capstone && (
            <button data-testid="open-capstone-from-completion" onClick={() => nav(`/becoming/${p.key}/capstone`)}
              className="mt-6 inline-flex items-center gap-3 bg-ink text-bg px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors">
              Open the Path {p.key} Capstone <ArrowRight size={14} />
            </button>
          )}
        </div>
      )}

      <div className="mt-10 border-t border-line">
        {p.modules.map((m) => {
          const isDone = done.includes(m.id);
          return (
            <button key={m.id} data-testid={`module-row-${p.key}-${m.id}`} onClick={() => nav(`/becoming/${p.key}/${m.id}`)}
              className="w-full text-left border-b border-line py-6 flex items-center gap-6 group hover:bg-surface/60 transition-colors px-2">
              <span className={`w-8 h-8 shrink-0 border flex items-center justify-center text-[11px] ${isDone ? "bg-accent border-accent text-bg" : "border-line text-ink-soft"}`}>
                {isDone ? <Check size={14} /> : m.label.replace("Module ", "")}
              </span>
              <span className="flex-1">
                <span className="block text-[10px] uppercase tracking-[0.2em] text-ink-soft">{m.label}</span>
                <span className="block font-serif text-xl text-ink">{m.title}</span>
              </span>
              <ArrowRight size={16} className="text-ink-soft transition-transform group-hover:translate-x-1" />
            </button>
          );
        })}
        {p.capstone && (
          <button data-testid={`capstone-row-${p.key}`} onClick={() => nav(`/becoming/${p.key}/capstone`)}
            className="w-full text-left border-b border-line py-6 flex items-center gap-6 group hover:bg-surface/60 transition-colors px-2">
            <span className={`w-8 h-8 shrink-0 border flex items-center justify-center ${capstoneDone ? "bg-accent border-accent text-bg" : "border-accent/50 text-accent"}`}>
              {capstoneDone ? <Check size={14} /> : "★"}
            </span>
            <span className="flex-1">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-accent">Capstone</span>
              <span className="block font-serif text-xl text-ink">{p.capstone.title}</span>
            </span>
            <ArrowRight size={16} className="text-ink-soft transition-transform group-hover:translate-x-1" />
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- Module page ----------
function ModuleReader({ slug, title }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true;
    setUrl(null); setErr(false);
    p2FileUrl(slug).then((u) => { if (active) setUrl(u); }).catch(() => { if (active) setErr(true); });
    return () => { active = false; };
  }, [slug]);
  return (
    <div className="mt-10">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <p className="text-[11px] uppercase tracking-[0.2em] text-ink-soft flex items-center gap-2"><FileText size={14} className="text-accent" /> Reading view — {title}</p>
        <div className="flex gap-3">
          <button onClick={() => openP2(slug, false)} className="text-[10px] uppercase tracking-[0.2em] text-ink-soft hover:text-ink">Open in new tab</button>
          <button onClick={() => openP2(slug, true)} className="text-[10px] uppercase tracking-[0.2em] text-ink-soft hover:text-ink inline-flex items-center gap-1"><Download size={11} /> Download</button>
        </div>
      </div>
      <div className="border border-line bg-surface" style={{ aspectRatio: "1 / 1.294" }}>
        {url ? <iframe data-testid={`reader-${slug}`} title={title} src={url + "#toolbar=1&view=FitH"} className="w-full h-full" />
          : <div className="w-full h-full flex items-center justify-center text-xs text-ink-soft">{err ? "Unable to load reading view." : "Loading reading view…"}</div>}
      </div>
    </div>
  );
}

// ---------- Capstone ----------
function CapstoneView({ course, completed, onToggle }) {
  const { pathKey } = useParams();
  const nav = useNavigate();
  const p = course.paths.find((x) => x.key === pathKey);
  if (!p || !p.capstone) return <p className="text-ink-soft">Capstone not found.</p>;
  const cap = p.capstone;
  const isDone = (completed[pathKey] || []).includes("capstone");
  return (
    <div data-testid={`capstone-view-${pathKey}`}>
      <button onClick={() => nav(`/becoming/${pathKey}`)} className="text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline flex items-center gap-1 mb-8"><ArrowLeft size={13} /> Path {pathKey}</button>
      <p className="text-[11px] uppercase tracking-ultra text-accent">Path {pathKey} · Capstone</p>
      <h1 className="mt-3 font-serif font-light text-4xl lg:text-5xl text-ink tracking-tight leading-[1.05]">{cap.title}</h1>
      <div className="lesson-prose max-w-2xl mt-8" dangerouslySetInnerHTML={{ __html: cap.html }} />
      <div className="mt-12 border-t border-line pt-8 flex flex-wrap items-center justify-between gap-6">
        <button data-testid="capstone-complete-btn" onClick={() => onToggle(pathKey, "capstone", !isDone)}
          className={`inline-flex items-center gap-3 px-7 py-3.5 text-[11px] uppercase tracking-[0.2em] border transition-colors ${isDone ? "border-accent text-accent" : "bg-ink text-bg border-ink hover:bg-[#33312E]"}`}>
          <Check size={15} /> {isDone ? "Capstone complete" : "Mark capstone complete"}
        </button>
        <button onClick={() => nav(`/becoming/${pathKey}`)} className="group inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-ink link-underline">
          Back to Path {pathKey} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </div>
  );
}

function ModuleView({ course, completed, onToggle }) {
  const { pathKey, moduleId } = useParams();
  const nav = useNavigate();
  const p = course.paths.find((x) => x.key === pathKey);
  if (moduleId === "capstone") return <CapstoneView course={course} completed={completed} onToggle={onToggle} />;
  const idx = p?.modules.findIndex((m) => m.id === moduleId);
  const m = p?.modules[idx];
  if (!m) return <p className="text-ink-soft">Module not found.</p>;
  const isDone = (completed[pathKey] || []).includes(m.id);
  const nextM = p.modules[idx + 1];
  const readPdf = m.resources.find((r) => r.role === "primary" && r.format === "PDF" && r.display && r.ready)
    || m.resources.find((r) => r.format === "PDF" && r.display && r.ready);

  return (
    <div data-testid={`module-view-${pathKey}-${m.id}`}>
      <button onClick={() => nav(`/becoming/${pathKey}`)} className="text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline flex items-center gap-1 mb-8"><ArrowLeft size={13} /> Path {pathKey}</button>
      <p className="text-[11px] uppercase tracking-ultra text-accent">Path {pathKey} · {m.label}</p>
      <h1 className="mt-3 font-serif font-light text-4xl lg:text-5xl text-ink tracking-tight leading-[1.05]">{m.title}</h1>
      <p className="mt-6 font-serif text-xl lg:text-2xl font-light text-ink-soft leading-snug max-w-3xl">{m.situation}</p>

      {/* The approved teaching (verbatim), before any resource/download. */}
      <Lesson lesson={m.lesson} />

      {m.caution && <p className="mt-8 text-sm text-ink bg-surface border border-line px-5 py-4 max-w-2xl">{m.caution}</p>}
      {m.scope_statement && (
        <div className="mt-6 border border-accent/40 bg-accent/5 px-5 py-4 max-w-2xl">
          <p className="text-sm text-ink font-light">{m.scope_statement}</p>
          {m.emergency_note && <p className="mt-2 text-sm font-medium text-ink">{m.emergency_note}</p>}
        </div>
      )}

      {/* In-platform PDF reading view (A4), for modules with a displayable PDF. */}
      {readPdf && <ModuleReader slug={readPdf.slug} title={readPdf.title} />}

      {/* Resource(s) — the workbook/tool to use for this module. */}
      <div className="mt-12">
        <p className="text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-4">Use this resource</p>
        <div className="space-y-4">
          {m.resources.map((r) => <ResourcePanel key={r.slug + r.role} r={r} />)}
        </div>
      </div>

      {/* Completion milestone (approved) + mark complete + next */}
      {m.lesson?.milestone && (
        <div data-testid="completion-milestone" className="mt-12 border border-ink/15 bg-surface p-6 max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.2em] text-accent flex items-center gap-2"><Check size={13} /> Completion milestone</p>
          <p className="mt-2 text-base font-light text-ink leading-relaxed">Before marking this module complete, you should have: {m.lesson.milestone}</p>
        </div>
      )}
      <div className="mt-8 border-t border-line pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <button data-testid="mark-complete-btn" onClick={() => onToggle(pathKey, m.id, !isDone)}
          className={`inline-flex items-center gap-3 px-7 py-3.5 text-[11px] uppercase tracking-[0.2em] border transition-colors ${isDone ? "border-accent text-accent" : "bg-ink text-bg border-ink hover:bg-[#33312E]"}`}>
          <Check size={15} /> {isDone ? "Module complete" : "Mark module complete"}
        </button>
        <button data-testid={nextM ? "next-module-btn" : "finish-path-btn"}
          onClick={() => nav(nextM ? `/becoming/${pathKey}/${nextM.id}` : `/becoming/${pathKey}/capstone`)}
          className="group inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-ink link-underline">
          {m.next} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </div>
  );
}

// ---------- My Resources ----------
function MyResources({ course }) {
  const nav = useNavigate();
  const groups = [
    { key: "start", label: "Start Here", items: course.start_here },
    ...course.paths.map((p) => {
      const seen = new Set();
      const items = [];
      p.modules.forEach((m) => m.resources.forEach((r) => { if (!seen.has(r.slug)) { seen.add(r.slug); items.push(r); } }));
      return { key: p.key, label: `Path ${p.key} · ${p.title}`, items };
    }),
  ];
  return (
    <div data-testid="p2-resources">
      <button onClick={() => nav("/becoming")} className="text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline flex items-center gap-1 mb-8"><ArrowLeft size={13} /> Dashboard</button>
      <h1 className="font-serif font-light text-4xl lg:text-5xl text-ink tracking-tight">My Resources</h1>
      <p className="mt-4 max-w-2xl text-base font-light text-ink-soft">Everything included with your purchase, in the order it appears in the course.</p>
      <div className="mt-10 space-y-14">
        {groups.map((g) => (
          <div key={g.key}>
            <p className="text-[11px] uppercase tracking-ultra text-accent mb-5">{g.label}</p>
            <div className="space-y-4">{g.items.map((r) => <ResourcePanel key={g.key + r.slug} r={r} />)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Navigator ----------
function NavigatorView({ course }) {
  const nav = useNavigate();
  const navRes = course.start_here.find((r) => r.slug === "course_navigator");
  return (
    <div data-testid="p2-navigator">
      <button onClick={() => nav("/becoming")} className="text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline flex items-center gap-1 mb-8"><ArrowLeft size={13} /> Dashboard</button>
      <p className="text-[11px] uppercase tracking-ultra text-accent">Start Here</p>
      <h1 className="mt-3 font-serif font-light text-4xl lg:text-5xl text-ink tracking-tight">The Course Navigator</h1>
      <p className="mt-5 max-w-2xl text-base font-light text-ink-soft leading-relaxed">
        Your principal orientation tool. It explains how the two paths work and helps you decide where to begin.
      </p>
      <div className="mt-8">{navRes && <ResourcePanel r={navRes} />}</div>
    </div>
  );
}

// ---------- Shell + loader ----------
export default function Becoming() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const location = useLocation();

  const load = useCallback(() => {
    api.get("/p2/course").then((r) => setData(r.data)).catch((e) => setErr(apiError(e)));
  }, []);
  useEffect(() => { load(); document.title = "Becoming an Estate Manager™ · Il Salotto Digitale"; }, [load]);
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  const choosePath = async (pathKey, go) => {
    try {
      await api.post("/p2/path", { path: pathKey });
      setData((d) => ({ ...d, chosen_path: pathKey }));
      if (go) window.location.assign(`/becoming/${pathKey}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (pathKey, moduleId, completed) => {
    try {
      const { data: res } = await api.post("/p2/complete", { path: pathKey, module_id: moduleId, completed });
      setData((d) => ({ ...d, completed: { ...d.completed, [pathKey]: res.completed } }));
      toast.success(completed ? "Module marked complete." : "Marked as not complete.");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (err) return (
    <div className="min-h-screen pt-32 text-center px-6">
      <p className="text-[11px] uppercase tracking-ultra text-accent">Il Salotto Digitale</p>
      <h1 className="mt-4 font-serif font-light text-3xl text-ink">{err}</h1>
      <Link to="/account" className="mt-6 inline-block text-[11px] uppercase tracking-[0.2em] link-underline">Back to My Library</Link>
    </div>
  );
  if (!data) return <div className="min-h-screen pt-32 text-center text-sm text-ink-soft">Loading your course…</div>;

  const cg = data.start_here.find((r) => r.slug === "course_guide");

  return (
    <div className="min-h-screen pt-20 bg-bg">
      {/* persistent course bar */}
      <div className="sticky top-20 z-20 bg-bg/95 backdrop-blur border-b border-line">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-3 flex items-center gap-6 flex-wrap">
          <Link to="/becoming" data-testid="p2-home-link" className="font-serif text-lg text-ink">Becoming an Estate Manager™</Link>
          {data.is_admin_preview && <span className="text-[10px] uppercase tracking-[0.2em] text-accent border border-accent/40 px-2 py-0.5">Admin preview</span>}
          <div className="ml-auto flex items-center gap-5">
            <Link to="/becoming/navigator" className="text-[11px] uppercase tracking-[0.18em] text-ink-soft hover:text-ink flex items-center gap-1.5"><Compass size={13} /> Navigator</Link>
            <Link to="/becoming/resources" data-testid="my-resources-link" className="text-[11px] uppercase tracking-[0.18em] text-ink-soft hover:text-ink">My Resources</Link>
            <button data-testid="course-guide-btn" onClick={() => cg && cg.ready && openP2("course_guide", true)}
              className={`text-[11px] uppercase tracking-[0.18em] flex items-center gap-1.5 ${cg && cg.ready ? "text-ink-soft hover:text-ink" : "text-ink-soft/50 cursor-default"}`}>
              <BookOpen size={13} /> Course Guide
            </button>
            <Link to="/account" className="text-[11px] uppercase tracking-[0.18em] text-ink-soft hover:text-ink">My Library</Link>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-6 lg:px-12 py-14 lg:py-20">
        <Routes>
          <Route index element={<Dashboard course={data} chosen={data.chosen_path} completed={data.completed} onChoosePath={choosePath} />} />
          <Route path="navigator" element={<NavigatorView course={data} />} />
          <Route path="resources" element={<MyResources course={data} />} />
          <Route path=":pathKey" element={<PathView course={data} completed={data.completed} />} />
          <Route path=":pathKey/:moduleId" element={<ModuleView course={data} completed={data.completed} onToggle={toggle} />} />
        </Routes>
      </div>
    </div>
  );
}
